import { Schema, SchemaField, Metadata } from '../types/Metadata';
import {
  getFieldValue,
  isEnterpriseSchema,
  getInvalidItemDataReference,
  getSchemaForm
} from '../utils';
import { FieldData } from '../types/FieldData';
import { useSelector } from '../utils/useSelector';
import { ViewType } from '../enums/ViewType';
import { DataType } from '../enums/DataType';
import { ResearchModelRefId } from '../enums/ResearchModelRefId';
import { WorkspacePrefix } from '../enums/WorkspacePrefix';

export const handleDisableEdit = (
  schemaField: SchemaField,
  item: Metadata,
  disabled: boolean,
  showHover: boolean
) => {
  const dataSchema = useSelector(s => s.view?.selected?.dataSchema);
  const dataType = dataSchema?.props?.type;
  const schemaForm = getSchemaForm(dataSchema, item);
  const showInEnabled = schemaForm?.props?.fields?.some(
    f => f.key === schemaField.key
  );
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const isSelectedDataType = schemaField.key.startsWith(dataType + '.');

  let warningMode = '';
  const ws = disabledForEditMode(item, selectedWorkspace, dataSchema);
  const alwaysDisabledKeys = [
    'Item.status',
    'Item.seriesBy',
    'Item.additionalMandatoryReviewers'
  ];

  const isEditable =
    !disabled && // Access control result
    !ws?.disabled && // Workspace validation
    !alwaysDisabledKeys.includes(schemaField.key) && // Disable always-disabled fields
    schemaField.dataType != DataType.enum && // Disable enum fields
    schemaField.parentField == null && // Disable for relationship, dataSources, etc...
    disabled == undefined &&
    ws?.desiredWorkspace == null && // Allow custom fields
    isSelectedDataType && // Check if the field is part of the selected data type
    showInEnabled; // Check if the field is enabled for item type

  if (ws?.desiredWorkspace) {
    warningMode = `Cannot edit outside of ${ws.desiredWorkspace} workspace.`;
  }

  const showEditIcon = showHover && isEditable;

  return { showEditIcon, isEditable, warningMode };
};

export const createUpdater = (
  setEditValueTree: React.Dispatch<React.SetStateAction<Partial<Metadata>>>
) => ({
  update: () => {},
  reset: () => {},
  updateField: () => {},
  updateFields: (fields: FieldData[]) => {
    setEditValueTree(d => {
      fields.forEach(({ field, value }) => {
        d.props[field.field] = value;
      });
      return { ...d, props: { ...d.props } };
    });
  }
});

export const updateSelectedCellPosition = (
  document: Document,
  setSelectedCellPosition
) => {
  const selectedCellElement =
    document.getElementsByClassName('selected-cell')[0];
  if (!!selectedCellElement) {
    var pos = selectedCellElement?.getBoundingClientRect();
    setSelectedCellPosition(pos);
  } else setSelectedCellPosition(null);
};

export const updateHoveredCellPosition = (
  document: Document,
  setHoveredCellPosition
) => {
  const hoveredCellElements = document.getElementsByClassName('hovered-cell');
  if (hoveredCellElements?.length) {
    const hoveredCellElement = hoveredCellElements[0];
    if (!!hoveredCellElement) {
      var pos = hoveredCellElement?.getBoundingClientRect();
      setHoveredCellPosition(pos);
    } else setHoveredCellPosition(null);
  }
};

export const handleScrollIntoView = (
  generateBoxAdjustments,
  selectedCellPosition,
  minEditBoxWidth,
  selectedCell,
  isTreeSelect,
  window,
  tableElement,
  document,
  setUpdateCellPositionTrigger,
  navFlyoutWidth,
  totalColumnCount = 0
) => {
  const boxAdjustments = generateBoxAdjustments(
    selectedCellPosition,
    minEditBoxWidth,
    selectedCell?.field?.schemaInfo?.schemaField,
    isTreeSelect,
    totalColumnCount
  );
  const { scrollX, scrollY } = window;
  const isSmallColumnTable = totalColumnCount <= 2;
  const needsScrollAdjustment =
    boxAdjustments?.editRightAdjustment != 0 ||
    boxAdjustments?.editBottomAdjustment != 0 ||
    selectedCellPosition?.left <= navFlyoutWidth ||
    selectedCellPosition?.top <=
      tableElement?.current?.getBoundingClientRect()?.bottom - 100;

  if (needsScrollAdjustment) {
    const selectedCellElement =
      document.getElementsByClassName('selected-cell')[0];

    if (isSmallColumnTable) {
      setUpdateCellPositionTrigger(true);
    } else {
      selectedCellElement.scrollIntoView({
        behavior: 'instant',
        block: 'center'
      });
      setUpdateCellPositionTrigger(true);
    }

    window.scrollTo(scrollX, scrollY);
  } else {
    setUpdateCellPositionTrigger(false);
  }
};

export const updateEditValue = (
  selectedCell,
  setEditValue,
  setOriginalValue
) => {
  if (
    !!selectedCell?.item?.props[
      selectedCell?.field?.schemaInfo?.schemaField?.field
    ]
  ) {
    const fieldValue = getFieldValue(
      selectedCell?.field?.schemaInfo?.schemaField,
      selectedCell?.item
    );
    setEditValue(fieldValue);
    setOriginalValue(fieldValue);
  } else {
    setEditValue(null);
    setOriginalValue(null);
  }
};

export const disabledForEditMode = (
  data: any,
  selectedWorkspace: any,
  dataSchema?: Schema
): { disabled: boolean; desiredWorkspace: string } => {
  if (dataSchema) {
    const invalidDataRef = getInvalidItemDataReference(
      selectedWorkspace,
      dataSchema,
      data
    );

    if (invalidDataRef) {
      const workspaces = useSelector(s => s.planning?.workspaces);
      const validWorkspace = getDesiredWorkspace(workspaces, invalidDataRef);

      if (validWorkspace) {
        return {
          disabled: true,
          desiredWorkspace: validWorkspace.props.name
        };
      }
    }
  }
  return {
    disabled: false,
    desiredWorkspace: null
  };
};

export const shouldShowEditModeButton = (selected, editMode) => {
  if (!selected) return false;
  const isImpersonating = useSelector(
    s => s.view?.viewingAs != null || s.view?.actingAs != null
  );

  return (
    selected?.props?.type === ViewType.Tabular &&
    !editMode &&
    !isEnterpriseSchema(selected.dataSchema) &&
    !selected.isBacktaggingView &&
    !isImpersonating
  );
};

export const areObjectsEqual = (obj1, obj2) => {
  if (obj1 == null && obj2 == null) return true;

  if (obj1 == null || obj2 == null) return false;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return Object.is(obj1, obj2);
  }

  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  const compareArrays = (arr1, arr2) => {
    if (!arr1 || !arr2)
      return (!arr1 && !arr2) || (arr1?.length === 0 && arr2?.length === 0);
    if (arr1.length !== arr2.length) return false;

    if (arr1[0]?.id) {
      const ids1 = new Set(arr1.map(item => item?.id).filter(Boolean));
      const ids2 = new Set(arr2.map(item => item?.id).filter(Boolean));

      if (ids1.size !== ids2.size) return false;

      for (const id of ids1) {
        if (!ids2.has(id)) return false;
      }

      return true;
    } else {
      const sortedArr1 = [...arr1].sort();
      const sortedArr2 = [...arr2].sort();

      for (let i = 0; i < sortedArr1.length; i++) {
        if (sortedArr1[i] !== sortedArr2[i]) {
          return false;
        }
      }
      return true;
    }
  };

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    return compareArrays(obj1, obj2);
  }

  return Object.keys(obj1).every(key => {
    if (!obj1[key] || (Array.isArray(obj1[key]) && obj1[key]?.length === 0)) {
      if (!obj2[key] || (Array.isArray(obj2[key]) && obj2[key]?.length === 0)) {
        return true;
      }
      return false;
    }

    if (Array.isArray(obj1[key])) {
      return compareArrays(obj1[key], obj2[key]);
    }
    if (typeof obj1[key] === 'object' && obj1[key] !== null) {
      if (obj1[key] instanceof Date && obj2[key] instanceof Date) {
        return obj1[key].getTime() === obj2[key].getTime();
      }
      return obj1[key]?.id === obj2[key]?.id;
    }
    return Object.is(obj1[key], obj2[key]);
  });
};

export const getDesiredWorkspace = (workspaces, invalidDataRef) => {
  const invalidResearchModel =
    invalidDataRef.props?.refId == ResearchModelRefId.RESEARCH_BOARD;
  const validWorkspaces = workspaces?.filter(w =>
    w.props?.enableItemDataRefIds?.includes(invalidDataRef.props?.refId)
  );
  if (invalidResearchModel) {
    const researchBoardWs = validWorkspaces?.find(
      w => w.props?.prefix == WorkspacePrefix.ResearchBoard
    );
    if (researchBoardWs) return researchBoardWs;
  }
  return validWorkspaces?.[0];
};
